/*
 * jayesstudio-megamenu-hover.js
 *
 * Progressive enhancement for the Horizon mega menu in `featured_products`
 * mode. Natively the featured-products strip shows ONE collection (the
 * dropdown's parent), and Horizon renders it empty server-side (eager_loading
 * is false off the Section Rendering API) then hydrates it. This enhancer makes
 * the strip respond to the shopper: hovering/focusing a collection link in the
 * mega-menu columns swaps the strip to that collection's top products, and
 * leaving the menu restores the parent collection's products.
 *
 * How: fetch the top products via Shopify's Section Rendering API
 * (`/collections/<handle>?section_id=jayesstudio-megamenu-products`), which
 * returns markup identical to the server-rendered strip, and swap it in.
 * Results are cached per collection; hovers are debounced; in-flight swaps are
 * superseded by newer hovers; keyboard focus is handled too. Because Horizon
 * lazy-renders the default strip as empty, this enhancer also OWNS the default:
 * it populates the parent collection's products on first engagement and
 * restores them on mouse-leave (never leaving the strip blank).
 *
 * Purely additive — if the strip isn't present (menu_style != featured_products)
 * it no-ops. Survives install-core. See CUSTOMIZATIONS.md.
 */
(function () {
  'use strict';

  var SECTION_ID = 'jayesstudio-megamenu-products';
  var HOVER_DELAY = 120; // ms — avoid fetching on incidental pass-through
  var cache = new Map(); // handle -> Promise<string innerHTML | null>

  function collectionHandle(href) {
    if (!href) return null;
    var m = href.match(/\/collections\/([^/?#]+)/);
    // Skip the catch-all "all" listing; it isn't a curated collection
    if (!m || m[1] === 'all') return null;
    return m[1];
  }

  function fetchStrip(handle) {
    if (cache.has(handle)) return cache.get(handle);
    var url = '/collections/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
    var p = fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var list = doc.querySelector('[data-megamenu-products]');
        return list ? list.innerHTML : null;
      })
      .catch(function () {
        cache.delete(handle); // allow a retry on a later hover
        return null;
      });
    cache.set(handle, p);
    return p;
  }

  function setupMenu(mega) {
    var strip = mega.querySelector('.mega-menu__content-list--products');
    if (!strip || mega.dataset.jsMegamenuHover === '1') return; // not featured_products, or already wired
    mega.dataset.jsMegamenuHover = '1';

    // The dropdown's parent menu link is the strip's DEFAULT collection. Horizon
    // renders the strip empty server-side, so we populate it ourselves rather
    // than snapshotting an empty default.
    var submenu = mega.closest('.menu-list__submenu');
    var ownerLi = submenu ? submenu.parentElement : null;
    var parentAnchor = ownerLi ? ownerLi.querySelector('a.menu-list__link') : null;
    var parentHandle = collectionHandle(parentAnchor && parentAnchor.getAttribute('href'));

    var activeHandle = null;
    var timer = null;
    var defaultEnsured = false;

    // Horizon's header-menu.js measures `--submenu-height` once, when the dropdown
    // opens, sizing the reveal `clip-path` (on .menu-list__submenu) to the
    // SERVER-rendered strip — which is one row. Our swap can grow the strip to two
    // rows, and that stale height crops the panel just below the first row (the 2nd
    // row's tiles are cut off). Re-measure after each swap so the clip-path reveals
    // the full panel. Scoped to THIS submenu (more specific than the core's value on
    // the header ancestor) so sibling dropdowns keep the core's measurement; the
    // submenu's own `max-height`/overflow still caps it to the viewport.
    function syncSubmenuHeight() {
      if (!submenu) return;
      // Double rAF: let the swapped-in cards lay out before measuring (mirrors the
      // core's own technique for avoiding a contain-intrinsic-size misread).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (submenu.offsetHeight > 0) {
            submenu.style.setProperty('--submenu-height', submenu.offsetHeight + 'px');
          }
        });
      });
    }

    function apply(handle) {
      activeHandle = handle;
      strip.setAttribute('aria-busy', 'true');
      fetchStrip(handle).then(function (inner) {
        if (activeHandle !== handle) return; // a newer hover won
        if (inner != null) {
          strip.innerHTML = inner;
          syncSubmenuHeight();
        }
        strip.removeAttribute('aria-busy');
      });
    }

    // Populate the parent collection's products the first time the menu is
    // engaged, so the default strip is never empty (Horizon lazy-loads it).
    function ensureDefault() {
      if (defaultEnsured || !parentHandle) return;
      defaultEnsured = true;
      if (!strip.querySelector('.mega-menu__content-list-item')) apply(parentHandle);
    }

    function swap(handle) { if (handle !== activeHandle) apply(handle); }
    function restore() { if (parentHandle && activeHandle !== parentHandle) apply(parentHandle); }

    // Default loads when the shopper engages the dropdown (hover/focus its top link).
    if (parentAnchor) {
      parentAnchor.addEventListener('mouseenter', ensureDefault);
      parentAnchor.addEventListener('focus', ensureDefault);
    }

    // Collection links live in the menu columns (.mega-menu__list); the strip's
    // own links point to /products/ and are correctly ignored by the handle regex.
    var links = mega.querySelectorAll('.mega-menu__list a[href*="/collections/"]');
    links.forEach(function (link) {
      var handle = collectionHandle(link.getAttribute('href'));
      if (!handle) return;
      link.addEventListener('mouseenter', function () {
        ensureDefault();
        clearTimeout(timer);
        timer = setTimeout(function () { swap(handle); }, HOVER_DELAY);
      });
      link.addEventListener('focus', function () {
        ensureDefault();
        clearTimeout(timer);
        swap(handle);
      });
    });

    // Leaving the whole dropdown returns to the default collection's products.
    mega.addEventListener('mouseleave', function () { clearTimeout(timer); restore(); });
  }

  function init(root) {
    (root || document).querySelectorAll('.mega-menu').forEach(setupMenu);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Re-wire after a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
